import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Paper,
  Stack,
  Typography,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { ENDPOINT, IMAGE_BASE_URL } from "../../../endpoints/endpoints";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const LeagueDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [league, setLeague] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleteDialog, setDeleteDialog] = useState(false);

  // Token check
  useEffect(() => {
    if (!localStorage.getItem("accessToken")) {
      toast.warning("Please log in to continue");
      navigate("/login");
    }
  }, [navigate]);

  // Fetch League Detail
  useEffect(() => {
    const fetchDetail = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem("accessToken");

        const res = await axios.get(ENDPOINT.LEAGUECATEGORY_DETAIL(id), {
          headers: { Authorization: `Bearer ${token}` },
        });

        // 🔥 FIXED: your backend uses "result" NOT "data"
        setLeague(res.data.result || res.data.data);
      } catch (err) {
        toast.error("Failed to fetch league detail");
      } finally {
        setLoading(false);
      }
    };

    fetchDetail();
  }, [id]);

  // Delete handler
  const handleDelete = async () => {
    try {
      const token = localStorage.getItem("accessToken");

      const res = await axios.delete(ENDPOINT.LEAGUECATEGORY_DELETE(id), {
        headers: { Authorization: `Bearer ${token}` },
      });

      toast.success("League deleted successfully!");

      setTimeout(() => navigate("/league/list"), 1200);
    } catch (err) {
      toast.error("Failed to delete league");
    } finally {
      setDeleteDialog(false);
    }
  };

  // Loading
  if (loading) {
    return (
      <Box display="flex" height="80vh" justifyContent="center" alignItems="center">
        <CircularProgress />
      </Box>
    );
  }

  // Not found
  if (!league) {
    return (
      <Container maxWidth="md" sx={{ mt: 5 }}>
        <Typography variant="h6" align="center" color="text.secondary">
          League not found.
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 5, mb: 5 }}>
      <ToastContainer />

      <Paper elevation={3} sx={{ p: 4, borderRadius: 3 }}>
        {/* Header */}
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h5" fontWeight={600}>
            🖼 League Detail
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate("/league/list")}
            sx={{ textTransform: "none" }}
          >
            ← Back to List
          </Button>
        </Stack>

        <Divider sx={{ mb: 3 }} />

        {/* Image */}
        <Box textAlign="center" mb={3}>
          <Box
            component="img"
            src={`${IMAGE_BASE_URL}${league.image}`} // 🔥 FIXED
            alt="league"
            sx={{
              width: "100%",
              maxWidth: 450,
              borderRadius: 2,
              border: "1px solid #ddd",
              objectFit: "cover",
              boxShadow: 2,
            }}
          />
        </Box>

        {/* Details */}
        <Stack spacing={2}>
          <Box>
            <Typography variant="subtitle2" color="text.secondary">
              Name
            </Typography>
            <Typography variant="body1">{league.name}</Typography>
          </Box>
        </Stack>

        <Divider sx={{ my: 3 }} />

        {/* Action Buttons */}
        <Stack direction="row" justifyContent="flex-end" spacing={2}>
          <Button
            variant="outlined"
            color="warning"
            onClick={() => navigate(`/league/update/${league.id}`)}
            sx={{ textTransform: "none" }}
          >
            Edit League
          </Button>

          <Button
            variant="contained"
            color="error"
            sx={{ textTransform: "none" }}
            onClick={() => setDeleteDialog(true)}
          >
            Delete
          </Button>
        </Stack>
      </Paper>

      {/* Delete Dialog */}
      <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this league? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default LeagueDetail;
